import { useContent } from "@/lib/ContentContext";
import { Pencil, X, Check, RotateCcw, Loader2, Eye } from "lucide-react";

export default function EditToolbar() {
  const { isAdmin, editMode, enterEdit, exitEdit, save, discardDrafts, dirtyCount, saving, loaded } =
    useContent();

  if (!loaded || !isAdmin) return null;

  if (!editMode) {
    return (
      <button
        onClick={enterEdit}
        className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 rounded-full bg-[#0B0A4A] hover:bg-[#3E1A7A] text-[#F4ECFF] border border-[#B87CFF]/40 backdrop-blur-md shadow-2xl px-4 py-2.5 text-xs font-medium tracking-wide transition-colors"
        data-testid="edit-toolbar-enter"
        aria-label="Enter edit mode"
      >
        <Pencil className="w-3.5 h-3.5" />
        Edit page
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex flex-wrap items-center gap-1 sm:gap-2 rounded-full bg-[#0B0A4A] text-[#F4ECFF] border border-[#B87CFF]/40 shadow-2xl px-3 py-2"
      data-testid="edit-toolbar"
    >
      <span className="inline-flex items-center gap-2 pl-2 pr-1 text-[11px] uppercase tracking-[0.2em] text-[#B87CFF]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#B87CFF] animate-pulse-soft" />
        Editing
      </span>

      <span
        className="hidden sm:inline-flex items-center text-[11px] text-[#F4ECFF]/70 px-2 border-l border-[#F4ECFF]/15"
        data-testid="edit-toolbar-dirty"
      >
        {dirtyCount === 0 ? "No changes" : `${dirtyCount} unsaved`}
      </span>

      <button
        onClick={save}
        disabled={saving || dirtyCount === 0}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#B87CFF] hover:bg-[#F4ECFF] text-[#0B0A4A] disabled:opacity-50 disabled:hover:bg-[#B87CFF] px-3.5 py-1.5 text-[11px] font-medium tracking-wide transition-colors"
        data-testid="edit-toolbar-save"
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        Save
      </button>

      <button
        onClick={discardDrafts}
        disabled={saving || dirtyCount === 0}
        className="inline-flex items-center gap-1.5 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-40 px-3 py-1.5 text-[11px] tracking-wide transition-colors"
        data-testid="edit-toolbar-discard"
      >
        <RotateCcw className="w-3 h-3" /> Discard
      </button>

      <button
        onClick={exitEdit}
        disabled={saving}
        className="inline-flex items-center gap-1.5 rounded-full bg-white/5 hover:bg-white/10 px-3 py-1.5 text-[11px] tracking-wide transition-colors"
        data-testid="edit-toolbar-exit"
        title="Exit edit mode"
      >
        <Eye className="w-3 h-3" /> Live
      </button>

      <button
        onClick={exitEdit}
        className="inline-flex items-center justify-center rounded-full hover:bg-white/10 p-1.5 sm:hidden"
        aria-label="Close"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
